import { t } from '../../i18n';
// src/components/ads/AdEditorModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DEVICES, makeEmptyAd } from './adsStorage';
import AdLayoutEditor from './AdLayoutEditor';
import { normalizeContentJson, normalizeLayoutJson } from './adLayoutDefaults';
function FieldLabel({ children }) {
    return <label className="text-xs text-gray-500">{children}</label>;
}
function ImgBox({ title, hint, previewUrl, metaLabel, onPick, onClear }) {
    return (<div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div><div className="text-sm font-semibold">{title || t('배경 이미지')}</div>{hint ? <div className="mt-0.5 text-[11px] text-gray-500">{hint}</div> : null}</div>
        <div className="flex gap-2">
          <label className="text-xs px-2 py-1 rounded border border-gray-200 hover:border-gray-400 cursor-pointer">{t("\uC5C5\uB85C\uB4DC")}<input type="file" accept="image/*" className="hidden" onChange={onPick}/>
          </label>
          <button onClick={onClear} className="text-xs px-2 py-1 rounded border border-gray-200 hover:border-gray-400" disabled={!previewUrl}>{t("\uC0AD\uC81C")}</button>
        </div>
      </div>

      <div className="p-3 h-[280px] grid place-items-center bg-white">
        {previewUrl ? (<img src={previewUrl} alt="preview" className="max-h-[260px] max-w-full object-contain"/>) : (<div className="text-sm text-gray-400">{t("\uC774\uBBF8\uC9C0 \uC5C6\uC74C")}</div>)}
      </div>

      <div className="px-3 pb-3 text-xs text-gray-400">{metaLabel || ' '}</div>
    </div>);
}
export default function AdEditorModal({ open, mode, area, draft, saving = false, onClose, onSave, }) {
    const [form, setForm] = useState(null);
    const [file, setFile] = useState(null);
    const [mobileFile, setMobileFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [mobilePreviewUrl, setMobilePreviewUrl] = useState('');
    const [layoutPreviewDevice, setLayoutPreviewDevice] = useState('desktop');
    const [elementFiles, setElementFiles] = useState({});
    const [assetPreviewUrls, setAssetPreviewUrls] = useState({});
    const [removedAssetIds, setRemovedAssetIds] = useState([]);
    const objectUrlsRef = useRef(new Set());
    const title = useMemo(() => (mode === 'edit' ? t("\uAD11\uACE0 \uC218\uC815") : t("\uC0C8 \uAD11\uACE0 \uB4F1\uB85D")), [mode]);
    useEffect(() => {
        if (!open)
            return;
        objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        objectUrlsRef.current.clear();
        const base = draft ? { ...draft } : makeEmptyAd({ area, writer: 'admin' });
        base.area = area;
        base.device = base.device || 'responsive';
        base.country = String(base.country || 'ALL').toUpperCase();
        base.order = Number.isFinite(Number(base.order)) ? Number(base.order) : 100;
        base.templateKey = base.templateKey || 'FREEFORM_V1';
        base.backgroundMode = base.backgroundMode || 'COVER';
        base.contentJson = normalizeContentJson(base.contentJson);
        base.layoutJson = normalizeLayoutJson(base.layoutJson);
        setForm(base);
        setFile(null);
        setMobileFile(null);
        const legacyDevice = String(base.device || '').toLowerCase();
        const desktopUrl = base.desktopImageUrl || (legacyDevice !== 'mobile' ? base.imageUrl : '') || '';
        const mobileUrl = base.mobileImageUrl || (legacyDevice === 'mobile' ? base.imageUrl : '') || '';
        setPreviewUrl(desktopUrl);
        setMobilePreviewUrl(mobileUrl);
        setLayoutPreviewDevice(legacyDevice === 'mobile' ? 'mobile' : 'desktop');
        setElementFiles({});
        setRemovedAssetIds([]);
        const existingAssetPreviews = {};
        (base.layoutJson?.assets || []).forEach((asset) => {
            if (asset?.id && asset?.url) existingAssetPreviews[asset.id] = asset.url;
        });
        setAssetPreviewUrls(existingAssetPreviews);
    }, [open, draft, area]);
    useEffect(() => () => {
        objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        objectUrlsRef.current.clear();
    }, []);
    if (!open || !form)
        return null;
    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const onPickFile = (e) => {
        const picked = e.target.files?.[0];
        e.target.value = '';
        if (!picked)
            return;
        if (!String(picked.type || '').startsWith('image/')) {
            alert(t("\uC774\uBBF8\uC9C0 \uD30C\uC77C\uB9CC \uC5C5\uB85C\uB4DC\uD560 \uC218 \uC788\uC5B4."));
            return;
        }
        setFile(picked);
        const reader = new FileReader();
        reader.onload = () => {
            setPreviewUrl(String(reader.result || ''));
        };
        reader.readAsDataURL(picked);
    };
    const onClearFile = () => {
        setFile(null);
        setPreviewUrl('');
        update({
            imagePath: '',
            imageUrl: '',
            desktopImagePath: '',
            desktopImageUrl: '',
            originalFileName: '',
            mimeType: '',
            fileSize: 0,
        });
    };
    const onPickMobileFile = (e) => {
        const picked = e.target.files?.[0];
        e.target.value = '';
        if (!picked) return;
        if (!String(picked.type || '').startsWith('image/')) {
            alert(t('이미지 파일만 업로드할 수 있어.'));
            return;
        }
        setMobileFile(picked);
        const reader = new FileReader();
        reader.onload = () => setMobilePreviewUrl(String(reader.result || ''));
        reader.readAsDataURL(picked);
    };
    const onClearMobileFile = () => {
        setMobileFile(null);
        setMobilePreviewUrl('');
        const legacyMobile = String(form?.device || '').toLowerCase() === 'mobile';
        update({
            mobileImagePath: '',
            mobileImageUrl: '',
            mobileOriginalFileName: '',
            mobileMimeType: '',
            mobileFileSize: 0,
            ...(legacyMobile ? {
                imagePath: '',
                imageUrl: '',
                desktopImagePath: '',
                desktopImageUrl: '',
                originalFileName: '',
                mimeType: '',
                fileSize: 0,
            } : {}),
        });
    };
    const onAddElementImage = (picked) => {
        const id = `asset_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const objectUrl = URL.createObjectURL(picked);
        objectUrlsRef.current.add(objectUrl);
        setElementFiles((prev) => ({ ...prev, [id]: picked }));
        setAssetPreviewUrls((prev) => ({ ...prev, [id]: objectUrl }));
        return { id, previewUrl: objectUrl };
    };
    const onRemoveElementImage = (assetId) => {
        const localFile = elementFiles[assetId];
        const preview = assetPreviewUrls[assetId];
        if (localFile && preview && String(preview).startsWith('blob:')) {
            URL.revokeObjectURL(preview);
            objectUrlsRef.current.delete(preview);
        }
        setElementFiles((prev) => { const next = { ...prev }; delete next[assetId]; return next; });
        setAssetPreviewUrls((prev) => { const next = { ...prev }; delete next[assetId]; return next; });
        if (!localFile) setRemovedAssetIds((prev) => prev.includes(assetId) ? prev : [...prev, assetId]);
    };
    const submit = () => {
        if (!String(form.label || '').trim()) {
            return alert(t("\uB0B4\uBD80\uC6A9 \uC774\uB984(label)\uC744 \uC785\uB825\uD574\uC918."));
        }
        if (!String(form.country || '').trim()) {
            return alert(t("\uAD6D\uAC00(country)\uB97C \uC785\uB825\uD574\uC918. \uC608: KR / US / JP / ALL"));
        }
        if (form.clickUrl && !/^(https?:\/\/|\/)/i.test(form.clickUrl)) {
            return alert(t('링크는 http://, https:// 또는 / 로 시작하는 내부 경로여야 해.'));
        }
        if (form.adminStatus === 'SCHEDULED' && !form.startAt) {
            return alert(t("\uC608\uC57D \uC0C1\uD0DC\uBA74 startAt(\uC2DC\uC791\uC77C\uC2DC)\uB97C \uB123\uC5B4\uC918."));
        }
        if (form.startAt && form.endAt) {
            const s = new Date(form.startAt).getTime();
            const e = new Date(form.endAt).getTime();
            if (!Number.isNaN(s) && !Number.isNaN(e) && s >= e) {
                return alert(t("endAt\uC740 startAt\uBCF4\uB2E4 \uB2A6\uC5B4\uC57C \uD574."));
            }
        }
        const legacyDevice = String(form.device || 'responsive').toLowerCase();
        const existingDesktop = Boolean(form.desktopImageUrl || (legacyDevice !== 'mobile' && form.imageUrl));
        const existingMobile = Boolean(form.mobileImageUrl || (legacyDevice === 'mobile' && form.imageUrl));
        const keepImage = existingDesktop && !file;
        const keepMobileImage = existingMobile && !mobileFile;
        if (legacyDevice === 'mobile') {
            if (!existingMobile && !mobileFile) return alert(t('모바일 광고 이미지를 업로드해줘.'));
        } else if (!keepImage && !file) {
            return alert(t('데스크톱/기본 광고 이미지를 업로드해줘.'));
        }
        onSave(form, legacyDevice === 'mobile' ? mobileFile : file, {
            keepImage: legacyDevice === 'mobile' ? existingMobile && !mobileFile : keepImage,
            mobileFile: legacyDevice === 'responsive' ? mobileFile : null,
            keepMobileImage: legacyDevice === 'responsive' ? keepMobileImage : true,
            elementFiles: Object.entries(elementFiles).map(([id, imageFile]) => ({ id, file: imageFile })),
            removedAssetIds,
        });
    };
    const metaLabel = file
        ? `${file.name} (${Math.round((file.size || 0) / 1024)}KB)`
        : form.originalFileName
            ? `${form.originalFileName}${form.fileSize ? ` (${Math.round(form.fileSize / 1024)}KB)` : ''}`
            : ' ';
    const mobileMetaLabel = mobileFile
        ? `${mobileFile.name} (${Math.round((mobileFile.size || 0) / 1024)}KB)`
        : form.mobileOriginalFileName
            ? `${form.mobileOriginalFileName}${form.mobileFileSize ? ` (${Math.round(form.mobileFileSize / 1024)}KB)` : ''}`
            : String(form.device || '') === 'mobile' && form.originalFileName
                ? `${form.originalFileName}${form.fileSize ? ` (${Math.round(form.fileSize / 1024)}KB)` : ''}`
                : ' ';
    return (<div className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center overflow-y-auto p-3 sm:p-5">
      <div className="fixed inset-0 bg-black/50" onClick={saving ? undefined : onClose}/>

      <div className="relative w-full sm:w-[1680px] max-w-[calc(100vw-16px)] sm:max-w-[99vw] max-h-[calc(100dvh-16px)] sm:max-h-[96vh] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="font-semibold">{title} · area: {area}</div>
          <button onClick={onClose} disabled={saving} className="px-2 py-1 rounded border border-gray-200 hover:border-gray-400 disabled:opacity-50">{t("\uB2EB\uAE30")}</button>
        </div>

        <div className="p-4 sm:p-5 overflow-auto max-h-[calc(100dvh-150px)] sm:max-h-[86vh]">
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">
            {t('Responsive 광고 1건에서 Desktop / Mobile 배경 이미지를 각각 등록 · 기존 Desktop/Mobile 전용 광고도 호환')}  
            <br />{t('새 Responsive 광고는 한 건에서 화면별 이미지를 자동 선택하고, 기존 device 전용 광고는 그대로 유지됩니다.')}</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel>Area</FieldLabel>
              <input value={form.area} disabled className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-gray-100 text-gray-500"/>
            </div>

            <div>
              <FieldLabel>Device</FieldLabel>
              <select value={form.device} onChange={(e) => update({ device: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-white">
                {DEVICES.map((item) => (<option key={item.value} value={item.value}>
                    {item.label}
                  </option>))}
              </select>
            </div>

            <div>
              <FieldLabel>{t('배너 렌더링 방식')}</FieldLabel>
              <select value={form.templateKey || 'FREEFORM_V1'} onChange={(e) => update({ templateKey: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-white">
                <option value="FREEFORM_V1">{t('배경 이미지 + 다국어 HTML/CSS 요소')}</option>
                <option value="IMAGE_ONLY">{t('이미지 전체만 표시')}</option>
              </select>
            </div>

            <div>
              <FieldLabel>{t('배경 이미지 맞춤')}</FieldLabel>
              <select value={form.backgroundMode || 'COVER'} onChange={(e) => update({ backgroundMode: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-white">
                <option value="COVER">{t('영역 채우기 · cover')}</option>
                <option value="CONTAIN">{t('이미지 전체 표시 · contain')}</option>
              </select>
            </div>

            <div>
              <FieldLabel>Country</FieldLabel>
              <input value={form.country} onChange={(e) => update({ country: e.target.value.toUpperCase() })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 focus:outline-none focus:border-gray-500" placeholder={t("\uC608: KR / US / JP / ALL")}/>
            </div>

            <div>
              <FieldLabel>{t("\uD45C\uC2DC \uC21C\uC11C(order)")}</FieldLabel>
              <input type="number" value={form.order} onChange={(e) => update({ order: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 focus:outline-none focus:border-gray-500"/>
            </div>

            <div className="sm:col-span-2">
              <FieldLabel>{t("\uB0B4\uBD80\uC6A9 \uC774\uB984(label)")}</FieldLabel>
              <input value={form.label} onChange={(e) => update({ label: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 focus:outline-none focus:border-gray-500" placeholder={t("\uC608: \uBA54\uC778 KR \uBAA8\uBC14\uC77C \uC0C1\uB2E8 \uBC30\uB108")}/>
            </div>

            <div className="sm:col-span-2">
              <FieldLabel>{t("\uD074\uB9AD \uC774\uB3D9 \uB9C1\uD06C(URL)")}</FieldLabel>
              <input value={form.clickUrl} onChange={(e) => update({ clickUrl: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 focus:outline-none focus:border-gray-500" placeholder={t('https://... 또는 /lounge')}/>
              <div className="text-xs text-gray-400 mt-1">{t('비워두면 이동 없음 · 외부 URL 또는 /lounge 같은 내부 경로 사용 가능')}</div>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-gray-200 p-4">
            <div className="font-semibold mb-3">{t("\uC608\uC57D/\uC0C1\uD0DC")}</div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <FieldLabel>{t("\uAD00\uB9AC \uC0C1\uD0DC")}</FieldLabel>
                <select value={form.adminStatus} onChange={(e) => update({ adminStatus: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-white">
                  <option value="ACTIVE">{t("\uB178\uCD9C(\uC989\uC2DC/\uAE30\uAC04\uBB34\uAD00)")}</option>
                  <option value="SCHEDULED">{t("\uC608\uC57D")}</option>
                  <option value="PENDING">{t("\uC608\uC57D \uBCF4\uB958")}</option>
                  <option value="DISABLED">{t("\uBE44\uD65C\uC131")}</option>
                </select>
              </div>

              <div>
                <FieldLabel>{t("startAt (\uC2DC\uC791)")}</FieldLabel>
                <input type="datetime-local" value={form.startAt || ''} onChange={(e) => update({ startAt: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200"/>
              </div>

              <div>
                <FieldLabel>{t("endAt (\uC885\uB8CC, \uC120\uD0DD)")}</FieldLabel>
                <input type="datetime-local" value={form.endAt || ''} onChange={(e) => update({ endAt: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200"/>
              </div>
            </div>

            <div className="text-xs text-gray-400 mt-2">{t("- PENDING \uB294 \uC800\uC7A5\uB9CC \uB418\uACE0 \uB178\uCD9C\uB418\uC9C0 \uC54A\uC74C")}<br />{t("- SCHEDULED \uB294 startAt \uC774\uD6C4 \uC790\uB3D9 \uB178\uCD9C")}<br />{t("- endAt \uC774 \uC9C0\uB098\uBA74 \uC790\uB3D9 \uB9CC\uB8CC")}</div>
          </div>

          <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
            {String(form.device || 'responsive') !== 'mobile' ? (
              <ImgBox title={t('Desktop 배경 이미지')} hint={t('PC/넓은 화면에서 사용')} previewUrl={previewUrl} metaLabel={metaLabel} onPick={onPickFile} onClear={onClearFile}/>
            ) : null}
            {String(form.device || 'responsive') !== 'desktop' ? (
              <ImgBox title={t('Mobile 배경 이미지')} hint={String(form.device || '') === 'responsive' ? t('모바일에서 우선 사용 · 비워두면 Desktop 이미지 fallback') : t('모바일 전용 광고 이미지')} previewUrl={mobilePreviewUrl} metaLabel={mobileMetaLabel} onPick={onPickMobileFile} onClear={onClearMobileFile}/>
            ) : null}
          </div>

          {String(form.templateKey || 'FREEFORM_V1').toUpperCase() === 'FREEFORM_V1' ? (
            <div className="mt-4">
              {String(form.device || 'responsive') === 'responsive' ? (
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-xs text-gray-500">{t('레이아웃 미리보기')}</span>
                  <select value={layoutPreviewDevice} onChange={(e) => setLayoutPreviewDevice(e.target.value)} className="rounded border bg-white px-2 py-1.5 text-xs">
                    <option value="desktop">Desktop</option>
                    <option value="mobile">Mobile</option>
                  </select>
                </div>
              ) : null}
              <AdLayoutEditor
                form={form}
                previewDevice={String(form.device || 'responsive') === 'responsive' ? layoutPreviewDevice : null}
                previewUrl={String(form.device || 'responsive') === 'responsive' && layoutPreviewDevice === 'mobile' ? (mobilePreviewUrl || previewUrl) : (String(form.device || '') === 'mobile' ? mobilePreviewUrl : previewUrl)}
                onChange={update}
                assetPreviewUrls={assetPreviewUrls}
                onAddElementImage={onAddElementImage}
                onRemoveElementImage={onRemoveElementImage}
              />
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
              {t('이미지 전체 표시 방식에서는 업로드한 이미지가 그대로 노출됩니다. 좌표·다국어 요소 편집은 배경 이미지 + HTML/CSS 방식을 선택할 때 사용합니다.')}
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button onClick={submit} disabled={saving} className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-black disabled:opacity-50">
              {saving ? t("\uC800\uC7A5 \uC911...") : t("\uC800\uC7A5")}
            </button>
            <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded border border-gray-200 hover:border-gray-400 disabled:opacity-50">{t("\uCDE8\uC18C")}</button>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">{t("\u203B \uC774\uBBF8\uC9C0\uB294 admin server \uC758")}<code>uploads/ads</code>{t("\uD3F4\uB354\uC5D0 \uC800\uC7A5\uB418\uACE0, DB\uC5D0\uB294 \uBA54\uD0C0/\uACBD\uB85C\uB9CC \uC800\uC7A5\uB3FC.")}</div>
      </div>
    </div>);
}
